/**
 * 版本管理弹窗 - 查看、恢复、对比、导出版本
 */
import React, { useState, useEffect, useCallback } from 'react';
import { fmtDateTimeCST } from '../utils/datetime';
import {
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
  CodeBracketIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { versionApi, VersionSummary, VersionDetail, getErrorMessage } from '../services/api';
import DiffViewer from './DiffViewer';
import WordExportDialog from './WordExportDialog';

interface VersionManagerProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  onRestore: (outlineNodes: any[], chapterContents: Record<string, string>) => void;
  currentTokens?: number;
  currentChapters?: number;
  activeSection?: string; // 当前活跃的 section（tech/biz），用于过滤版本列表
  /** 当前项目的章节内容（未保存版本），用于与已保存版本做对比 */
  currentChapterContents?: Record<string, string>;
  /** 当前项目的目录节点（用于版本对比时构造"当前版本"） */
  currentOutlineNodes?: Array<{
    node_id: string;
    parent_node_id: string | null;
    title: string;
    description: string;
    word_count_range?: string | null;
    source_requirement_id?: string | null;
    source_requirement_title?: string | null;
  }>;
  /** 当在 DiffViewer 中编辑"当前版本"时，保存内容的回调 */
  onUpdateCurrent?: (nodeId: string, content: string) => Promise<void>;
}

const VersionManager: React.FC<VersionManagerProps> = ({
  projectId,
  isOpen,
  onClose,
  onRestore,
  currentTokens = 0,
  currentChapters = 0,
  activeSection,
  currentChapterContents,
  currentOutlineNodes,
  onUpdateCurrent,
}) => {
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  // 对比弹窗状态
  const [showDiffViewer, setShowDiffViewer] = useState(false);
  const [diffVersionA, setDiffVersionA] = useState<{ detail: VersionDetail; name: string; id: number } | null>(null);
  const [diffVersionB, setDiffVersionB] = useState<{ detail: VersionDetail; name: string; id: number } | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  // 对比选择模式：选中要对比的两个版本
  const [diffSelectMode, setDiffSelectMode] = useState(false);
  const [selectedForDiff, setSelectedForDiff] = useState<number[]>([]);
  // 帮助弹窗
  const [showHelpPopover, setShowHelpPopover] = useState(false);
  // 保存版本：输入描述
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);
  // 导出版本 Word：模板选择对话框
  const [showWordExportDialog, setShowWordExportDialog] = useState(false);
  const [wordExportVersionId, setWordExportVersionId] = useState<number | null>(null);
  const [wordExporting, setWordExporting] = useState(false);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await versionApi.listVersions(projectId, activeSection);
      setVersions(res.data.versions);
    } catch (err) {
      setError(getErrorMessage(err, '加载版本列表失败'));
    } finally {
      setLoading(false);
    }
  }, [projectId, activeSection]);

  useEffect(() => {
    if (isOpen) {
      loadVersions();
      setShowDiffViewer(false);
      setDiffVersionA(null);
      setDiffVersionB(null);
      setDiffSelectMode(false);
      setSelectedForDiff([]);
    }
  }, [isOpen, loadVersions]);

  // 打开保存版本对话框
  const handleOpenSaveDialog = () => {
    setSaveDescription('');
    setShowSaveDialog(true);
    setError(null);
  };

  // 确认保存版本
  const handleSaveVersion = async () => {
    setSaving(true);
    setError(null);
    try {
      const timestamp = fmtDateTimeCST(new Date()).replace(/\//g, '');
      const desc = saveDescription.trim();

      const sectionLabel = activeSection === 'biz' ? '商务' : activeSection === 'tech' ? '技术' : '';
      const sectionPrefix = sectionLabel ? `${sectionLabel}_` : '';
      await versionApi.saveVersion(projectId, {
        version_name: `${sectionPrefix}版本_${timestamp}`,
        version_description: desc || undefined,
        total_tokens: currentTokens,
        generated_chapters: currentChapters,
        section: activeSection || undefined,
      });
      setShowSaveDialog(false);
      await loadVersions();
    } catch (err) {
      setError(getErrorMessage(err, '保存版本失败'));
    } finally {
      setSaving(false);
    }
  };

  // 恢复版本
  const handleRestore = async (version: VersionSummary) => {
    if (!window.confirm(`确定要恢复到版本「${version.version_name}」吗？当前未保存的更改将丢失。`)) {
      return;
    }
    setRestoringId(version.id);
    setError(null);
    try {
      const res = await versionApi.restoreVersion(projectId, version.id);
      onRestore(res.data.outline_nodes, res.data.chapter_contents);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, '恢复版本失败'));
    } finally {
      setRestoringId(null);
    }
  };

  // 进入对比选择模式
  const handleEnterDiffMode = () => {
    setDiffSelectMode(true);
    setSelectedForDiff([]);
    setError(null);
  };

  // 取消对比选择模式
  const handleCancelDiffMode = () => {
    setDiffSelectMode(false);
    setSelectedForDiff([]);
  };

  // 切换选择版本用于对比（支持选 1 或 2 个）
  const toggleSelectForDiff = (versionId: number) => {
    setSelectedForDiff(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      }
      if (prev.length >= 2) {
        // 如果已经选了2个，替换最后一个
        return [prev[1], versionId];
      }
      return [...prev, versionId];
    });
  };

  // 执行对比
  const handleDoCompare = async () => {
    if (selectedForDiff.length === 0 || selectedForDiff.length > 2) return;
    setDiffLoading(true);
    setError(null);
    try {
      if (selectedForDiff.length === 2) {
        // 两个都是已保存版本
        const [resA, resB] = await Promise.all([
          versionApi.getVersionDetail(projectId, selectedForDiff[0]),
          versionApi.getVersionDetail(projectId, selectedForDiff[1]),
        ]);
        const verA = versions.find(v => v.id === selectedForDiff[0]);
        const verB = versions.find(v => v.id === selectedForDiff[1]);
        setDiffVersionA({ detail: resA.data, id: selectedForDiff[0], name: verA?.version_name || `版本#${selectedForDiff[0]}` });
        setDiffVersionB({ detail: resB.data, id: selectedForDiff[1], name: verB?.version_name || `版本#${selectedForDiff[1]}` });
      } else {
        // 只选了 1 个版本，第二侧使用当前未保存的内容
        const resA = await versionApi.getVersionDetail(projectId, selectedForDiff[0]);
        const verA = versions.find(v => v.id === selectedForDiff[0]);
        // 构造"当前版本"的 VersionDetail
        const currentVersionDetail: VersionDetail = {
          id: 0, // 标记为当前版本（未保存）
          version_name: '当前版本（未保存）',
          version_description: null,
          total_tokens: currentTokens,
          generated_chapters: currentChapters,
          created_at: null,
          outline_nodes: currentOutlineNodes || [],
          chapter_contents: currentChapterContents || {},
        };
        setDiffVersionA({ detail: resA.data, id: selectedForDiff[0], name: verA?.version_name || `版本#${selectedForDiff[0]}` });
        setDiffVersionB({ detail: currentVersionDetail, id: 0, name: '当前版本（未保存）' });
      }
      setShowDiffViewer(true);
      setDiffSelectMode(false);
    } catch (err) {
      setError(getErrorMessage(err, '加载版本详情失败'));
    } finally {
      setDiffLoading(false);
    }
  };

  // 导出版本 Markdown：导出接口需要认证，必须带 Bearer token（不能用 <a href> 直接打开）
  const handleExport = async (versionId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(versionApi.exportVersionMarkdown(projectId, versionId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        throw new Error(`导出失败（HTTP ${res.status}）`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `version_${versionId}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(getErrorMessage(error, '导出版本 Markdown 失败'));
    }
  };

  // 导出版本 Word - 弹出模板选择对话框
  const handleExportWord = (versionId: number) => {
    setWordExportVersionId(versionId);
    setShowWordExportDialog(true);
    setError(null);
  };

  // 模板选择确认后提交 Word 导出
  const handleWordExportConfirm = async (templateId: string) => {
    if (wordExportVersionId === null) return;
    setShowWordExportDialog(false);
    setWordExporting(true);
    setError(null);
    try {
      await versionApi.exportVersionWord(projectId, wordExportVersionId, templateId);
      setShowWordExportDialog(false);
      setWordExportVersionId(null);
      // 任务已提交，用户可在任务列表中查看进度和下载
    } catch (err) {
      setError(getErrorMessage(err, '提交 Word 导出任务失败'));
    } finally {
      setWordExporting(false);
    }
  };

  // 删除版本
  const handleDelete = async (version: VersionSummary) => {
    if (!window.confirm(`确定要删除版本「${version.version_name}」吗？此操作不可撤销。`)) {
      return;
    }
    try {
      await versionApi.deleteVersion(projectId, version.id);
      await loadVersions();
    } catch (err) {
      setError(getErrorMessage(err, '删除版本失败'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* 弹窗 */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">版本管理</h2>
            <p className="text-sm text-gray-500 mt-0.5">管理标书内容的版本快照</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 操作栏 */}
        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenSaveDialog}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <CheckCircleIcon className="w-4 h-4 mr-1.5" />
              保存当前版本
            </button>
            {!diffSelectMode ? (
              <>
                <button
                  onClick={handleEnterDiffMode}
                  disabled={versions.length < 1}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="选择版本与当前内容或另一版本进行对比"
                >
                  <ArrowsRightLeftIcon className="w-4 h-4 mr-1.5" />
                  版本对比
                </button>
                {/* 帮助按钮 */}
                <div className="relative">
                  <button
                    onClick={() => setShowHelpPopover(!showHelpPopover)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title="使用帮助"
                  >
                    <QuestionMarkCircleIcon className="w-4 h-4" />
                  </button>
                  {showHelpPopover && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowHelpPopover(false)}
                      />
                      <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-4 text-sm text-gray-700">
                        <h4 className="font-semibold text-gray-900 mb-2">使用方式</h4>
                        <ol className="list-decimal list-inside space-y-1.5 text-xs leading-relaxed">
                          <li>点击「版本管理」打开版本列表</li>
                          <li>点击顶部的「版本对比」按钮进入选择模式</li>
                          <li>勾选 1~2 个版本（标记为 <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-purple-500 text-white text-[10px] font-bold mx-0.5 align-middle">A</span> 和可选 <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-purple-500 text-white text-[10px] font-bold mx-0.5 align-middle">B</span>）</li>
                          <li>选 1 个版本时，第二个默认为当前未保存的内容</li>
                          <li>点击「开始对比」，打开全屏对比弹窗</li>
                          <li>支持并排视图和统一视图，逐章节查看差异</li>
                        </ol>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={handleDoCompare}
                  disabled={selectedForDiff.length === 0 || selectedForDiff.length > 2 || diffLoading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {diffLoading ? (
                    <div className="w-4 h-4 mr-1.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowsRightLeftIcon className="w-4 h-4 mr-1.5" />
                  )}
                  开始对比 ({selectedForDiff.length}/2)
                </button>
                <button
                  onClick={handleCancelDiffMode}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  取消
                </button>
              </>
            )}
          </div>
          <span className="text-xs text-gray-400">
            共 {versions.length} 个版本
          </span>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mx-6 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 flex-shrink-0">
            {error}
          </div>
        )}

        {/* 版本列表 */}
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-3">
          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">加载中...</div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">暂无版本记录</p>
              <p className="text-xs text-gray-400 mt-1">点击"保存当前版本"创建第一个版本快照</p>
            </div>
          ) : (
            versions.map((v) => (
              <div key={v.id}>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between">
                    {/* 版本信息 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {v.version_name}
                        </h3>
                        {v.generated_chapters > 0 && (
                          <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
                            {v.generated_chapters} 章节
                          </span>
                        )}
                      </div>
                      {v.version_description && (
                        <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                          {v.version_description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center">
                          <ClockIcon className="w-3 h-3 mr-0.5" />
                          {v.created_at
                            ? fmtDateTimeCST(v.created_at)
                            : '-'}
                        </span>
                        {v.total_tokens > 0 && (
                          <span>Tokens: {v.total_tokens.toLocaleString()}</span>
                        )}
                      </div>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                      {/* 对比选择模式下：复选框 */}
                      {diffSelectMode && (
                        <button
                          onClick={() => toggleSelectForDiff(v.id)}
                          className={`p-1.5 rounded text-xs transition-colors ${
                            selectedForDiff.includes(v.id)
                              ? 'text-white bg-purple-500 hover:bg-purple-600'
                              : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                          }`}
                          title={selectedForDiff.includes(v.id) ? '已选中（点击取消）' : '选择对比'}
                        >
                          {selectedForDiff.includes(v.id) ? (
                            <span className="text-xs font-bold">
                              {selectedForDiff.indexOf(v.id) === 0 ? 'A' : 'B'}
                            </span>
                          ) : (
                            <ArrowsRightLeftIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}

                      {/* 导出 Word */}
                      <button
                        onClick={() => handleExportWord(v.id)}
                        disabled={wordExporting && wordExportVersionId === v.id}
                        className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                        title="导出当前版本Word"
                      >
                        {wordExporting && wordExportVersionId === v.id ? (
                          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <DocumentArrowDownIcon className="w-4 h-4" />
                        )}
                      </button>

                      {/* 导出 Markdown */}
                      <button
                        onClick={() => handleExport(v.id)}
                        className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                        title="导出当前版本Markdown"
                      >
                        <CodeBracketIcon className="w-4 h-4" />
                      </button>

                      {/* 恢复 */}
                      <button
                        onClick={() => handleRestore(v)}
                        disabled={restoringId === v.id}
                        className="p-1.5 rounded text-gray-400 hover:text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors"
                        title="恢复此版本"
                      >
                        {restoringId === v.id ? (
                          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ArrowPathIcon className="w-4 h-4" />
                        )}
                      </button>

                      {/* 删除 */}
                      <button
                        onClick={() => handleDelete(v)}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="删除"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            ))
          )}
        </div>
      </div>

      {/* 保存版本描述对话框 */}
      {showSaveDialog && (
        <div className="absolute inset-0 flex items-center justify-center z-30">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowSaveDialog(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">保存版本</h3>
            <p className="text-sm text-gray-500 mb-4">
              请输入版本描述，方便后续查找和标记此版本
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                版本描述 <span className="text-gray-400 font-normal">（选填）</span>
              </label>
              <input
                type="text"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSaveVersion(); }}
                placeholder="例如：完成第一章、修改资质部分..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1">
                版本号将自动添加时间戳，如：2026/06/20 13:17_完成第一章
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-4 py-2 text-sm font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveVersion}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 mr-1.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    保存中...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                    确认保存
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 对比弹窗 */}
      {showDiffViewer && diffVersionA && diffVersionB && (
        <DiffViewer
          isOpen={showDiffViewer}
          onClose={() => setShowDiffViewer(false)}
          projectId={projectId}
          versionA={diffVersionA.detail}
          versionAId={diffVersionA.id}
          versionAName={diffVersionA.name}
          versionB={diffVersionB.detail}
          versionBId={diffVersionB.id}
          versionBName={diffVersionB.name}
          onUpdateCurrentContent={onUpdateCurrent}
        />
      )}

      {/* Word 导出模板选择对话框 */}
      {showWordExportDialog && (
        <WordExportDialog
          open={showWordExportDialog}
          onClose={() => { setShowWordExportDialog(false); setWordExportVersionId(null); }}
          onConfirm={handleWordExportConfirm}
        />
      )}
    </div>
  );
};

export default VersionManager;
